import { useMemo } from "react";
import { useFetch } from "../../../hooks/api";
import { useTranslation } from "react-i18next";
import { keepPreviousData } from "@tanstack/react-query";
import { MdRotateLeft } from "react-icons/md";
import { HoverInfo } from "../../core/basic/Tooltip";
import { Button } from "../../core/basic/Button";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";

const columnHelper = createAppColumnHelper();

const globalFilterFn = commaSearchFilterFn((row) => row.org?.profile_name ?? "");

const WalmartProfilesTable = () => {
  const { t } = useTranslation(["onboarding", "common"]);

  const { data: walmartProfiles, isLoading: isProfilesDataLoading } = useFetch(
    ["allWalmartProfileAdsInfo"],
    `/walmart/auth/onboarding`,
    {
      all_profiles: true,
    },
    {
      placeholderData: keepPreviousData,
      select: (res) => res.data,
    },
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "walmartProfilesTable",
    initialState: { sorting: [{ id: "profile_name", desc: false }] },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.org?.profile_name, {
        id: "profile_name",
        header: t(($) => $.onboarding.Profile),
        size: 300,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((row) => row.org?.profile_type, {
        id: "profile_type",
        header: t(($) => $.onboarding["Profile Type"]),
        size: 140,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) =>
          getValue() === "seller" ? t(($) => $.onboarding["3P Seller"]) : t(($) => $.onboarding["1P Supplier"]),
      }),
      columnHelper.accessor("advertiser_id_provided", {
        header: t(($) => $.onboarding["Advertising API Connected"]),
        size: 200,
        enableResizing: false,
        cell: ({ getValue }) => (getValue() ? t(($) => $.common.Yes) : t(($) => $.common.No)),
      }),
      columnHelper.accessor("marketplace_credentials_provided", {
        header: t(($) => $.onboarding["Marketplace API Connected"]),
        size: 220,
        enableResizing: false,
        cell: ({ row }) => {
          const { marketplace_credentials_provided } = row.original;
          const profile_type = row.original.org?.profile_type;

          return (
            <div className={css({ display: "flex", gap: "small" })}>
              <span>
                {profile_type === "seller" ?
                  marketplace_credentials_provided ?
                    t(($) => $.common.Yes)
                  : t(($) => $.common.No)
                : t(($) => $.onboarding["N/A"])}
              </span>
              {profile_type !== "seller" ?
                <HoverInfo iconCss={{ color: "grey" }}>
                  {t(($) => $.onboarding["The Walmart Marketplace API currently only supports 3P sellers"])}
                </HoverInfo>
              : null}
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.org?.marketplaces?.join(", "), {
        id: "marketplaces",
        header: t(($) => $.onboarding.Marketplaces),
        size: 160,
        sortFn: "alphanumeric",
      }),
    ],
    [t],
  );

  const table = useClientTable({
    data: walmartProfiles,
    columns,
    globalFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isProfilesDataLoading,
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

export default WalmartProfilesTable;
